/* eslint-disable @typescript-eslint/no-use-before-define, no-restricted-syntax, import/no-mutable-exports, no-lonely-if */ // eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useLayoutEffect } from '../utils';
import { focusSafely } from './focusSafely';
import { isElementVisible } from './isElementVisible';
function getOwnerDocument(el) {
    return (el === null || el === void 0 ? void 0 : el.ownerDocument) || document;
}
const FocusContext = /*#__PURE__*/ React.createContext(null);
let activeScope = null;
// This is a hacky DOM-based implementation of a FocusScope until this RFC lands in React:
// https://github.com/reactjs/rfcs/pull/109
/**
 * A FocusScope manages focus for its descendants. It supports containing focus inside
 * the scope, restoring focus to the previously focused element on unmount, and auto
 * focusing children on mount. It also acts as a container for a programmatic focus
 * management interface that can be used to move focus forward and back in response
 * to user events.
 */ export function FocusScope(props) {
    const { children, contain, restoreFocus, autoFocus } = props;
    const startRef = useRef();
    const endRef = useRef();
    const scopeRef = useRef([]);
    const { parentNode } = useContext(FocusContext) || {};
    // Create a tree node here so we can add children to it even before it is added to the tree.
    const node = useMemo(()=>new TreeNode({
            scopeRef
        }), [
        scopeRef
    ]);
    useLayoutEffect(()=>{
        // If a new scope mounts outside the active scope, (e.g. DialogContainer launched from a menu),
        // use the active scope as the parent instead of the parent from context. Layout effects run bottom
        // up, so if the parent is not yet added to the tree, don't do this. Only the outer-most FocusScope
        // that is being added should get the activeScope as its parent.
        let parent = parentNode || focusScopeTree.root;
        if (focusScopeTree.getTreeNode(parent.scopeRef) && activeScope && !isAncestorScope(activeScope, parent.scopeRef)) {
            const activeNode = focusScopeTree.getTreeNode(activeScope);
            if (activeNode) {
                parent = activeNode;
            }
        }
        // Add the node to the parent, and to the tree.
        parent.addChild(node);
        focusScopeTree.addNode(node);
    }, [
        node,
        parentNode
    ]);
    useLayoutEffect(()=>{
        const node = focusScopeTree.getTreeNode(scopeRef);
        node.contain = contain;
    }, [
        contain
    ]);
    useLayoutEffect(()=>{
        // Find all rendered nodes between the sentinels and add them to the scope.
        let node = startRef.current.nextSibling;
        const nodes = [];
        while(node && node !== endRef.current){
            nodes.push(node);
            node = node.nextSibling;
        }
        scopeRef.current = nodes;
    }, [
        children
    ]);
    useActiveScopeTracker(scopeRef, restoreFocus, contain);
    useFocusContainment(scopeRef, contain);
    useRestoreFocus(scopeRef, restoreFocus, contain);
    useAutoFocus(scopeRef, autoFocus);
    // This needs to be an effect so that activeScope is updated after the FocusScope tree is complete.
    // It cannot be a useLayoutEffect because the parent of this node hasn't been attached in the tree yet.
    useEffect(()=>{
        const ownerDocument = getOwnerDocument(scopeRef.current[0]);
        const activeElement = ownerDocument.activeElement;
        let scope = null;
        if (isElementInScope(activeElement, scopeRef.current)) {
            // We need to traverse the focusScope tree and find the bottom most scope that
            // contains the active element and set that as the activeScope.
            for (const node of focusScopeTree.traverse()){
                if (node.scopeRef && isElementInScope(activeElement, node.scopeRef.current)) {
                    scope = node;
                }
            }
            if (scope === focusScopeTree.getTreeNode(scopeRef)) {
                activeScope = scope.scopeRef;
            }
        }
    }, [
        scopeRef
    ]);
    // This layout effect cleanup is so that the tree node is removed synchronously with react before the RAF
    // in useRestoreFocus cleanup runs.
    useLayoutEffect(()=>{
        return ()=>{
            var _focusScopeTree_getTreeNode_parent, _focusScopeTree_getTreeNode;
            var _focusScopeTree_getTreeNode_parent_scopeRef;
            // Scope may have been re-parented.
            const parentScope = (_focusScopeTree_getTreeNode_parent_scopeRef = (_focusScopeTree_getTreeNode = focusScopeTree.getTreeNode(scopeRef)) === null || _focusScopeTree_getTreeNode === void 0 ? void 0 : (_focusScopeTree_getTreeNode_parent = _focusScopeTree_getTreeNode.parent) === null || _focusScopeTree_getTreeNode_parent === void 0 ? void 0 : _focusScopeTree_getTreeNode_parent.scopeRef) !== null && _focusScopeTree_getTreeNode_parent_scopeRef !== void 0 ? _focusScopeTree_getTreeNode_parent_scopeRef : null;
            if ((scopeRef === activeScope || isAncestorScope(scopeRef, activeScope)) && (!parentScope || focusScopeTree.getTreeNode(parentScope))) {
                activeScope = parentScope;
            }
            focusScopeTree.removeTreeNode(scopeRef);
        };
    }, [
        scopeRef
    ]);
    const focusManager = useMemo(()=>createFocusManagerForScope(scopeRef), []);
    const value = useMemo(()=>({
            focusManager,
            parentNode: node
        }), [
        node,
        focusManager
    ]);
    return /*#__PURE__*/ React.createElement(FocusContext.Provider, {
        value: value
    }, /*#__PURE__*/ React.createElement("span", {
        "data-focus-scope-start": true,
        hidden: true,
        ref: startRef
    }), children, /*#__PURE__*/ React.createElement("span", {
        "data-focus-scope-end": true,
        hidden: true,
        ref: endRef
    }));
}
/**
 * Returns a FocusManager interface for the parent FocusScope.
 * A FocusManager can be used to programmatically move focus within
 * a FocusScope, e.g. in response to user events like keyboard navigation.
 */ export function useFocusManager() {
    var _useContext;
    const contextFocusManager = (_useContext = useContext(FocusContext)) === null || _useContext === void 0 ? void 0 : _useContext.focusManager;
    return contextFocusManager;
}
function createFocusManagerForScope(scopeRef) {
    return {
        focusNext (opts = {}) {
            const scope = scopeRef.current;
            const ownerDocument = getOwnerDocument(scope[0]);
            const { from, tabbable, wrap, accept } = opts;
            const node = from || ownerDocument.activeElement;
            const sentinel = scope[0].previousElementSibling;
            const walker = getFocusableTreeWalker(getScopeRoot(scope), {
                tabbable,
                accept
            }, scope);
            walker.currentNode = isElementInScope(node, scope) ? node : sentinel;
            let nextNode = walker.nextNode();
            if (!nextNode && wrap) {
                walker.currentNode = sentinel;
                nextNode = walker.nextNode();
            }
            if (nextNode) {
                focusElement(nextNode, true);
            }
            return nextNode;
        },
        focusPrevious (opts = {}) {
            const scope = scopeRef.current;
            const { from, tabbable, wrap, accept } = opts;
            const ownerDocument = getOwnerDocument(scope[0]);
            const node = from || ownerDocument.activeElement;
            const sentinel = scope[scope.length - 1].nextElementSibling;
            const walker = getFocusableTreeWalker(getScopeRoot(scope), {
                tabbable,
                accept
            }, scope);
            walker.currentNode = isElementInScope(node, scope) ? node : sentinel;
            let previousNode = walker.previousNode();
            if (!previousNode && wrap) {
                walker.currentNode = sentinel;
                previousNode = walker.previousNode();
            }
            if (previousNode) {
                focusElement(previousNode, true);
            }
            return previousNode;
        },
        focusFirst (opts = {}) {
            const scope = scopeRef.current;
            const { tabbable, accept } = opts;
            const walker = getFocusableTreeWalker(getScopeRoot(scope), {
                tabbable,
                accept
            }, scope);
            walker.currentNode = scope[0].previousElementSibling;
            const nextNode = walker.nextNode();
            if (nextNode) {
                focusElement(nextNode, true);
            }
            return nextNode;
        },
        focusLast (opts = {}) {
            const scope = scopeRef.current;
            const { tabbable, accept } = opts;
            const walker = getFocusableTreeWalker(getScopeRoot(scope), {
                tabbable,
                accept
            }, scope);
            walker.currentNode = scope[scope.length - 1].nextElementSibling;
            const previousNode = walker.previousNode();
            if (previousNode) {
                focusElement(previousNode, true);
            }
            return previousNode;
        }
    };
}
const focusableElements = [
    'input:not([disabled]):not([type=hidden])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'a[href]',
    'area[href]',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]'
];
const FOCUSABLE_ELEMENT_SELECTOR = `${focusableElements.join(':not([hidden]),')},[tabindex]:not([disabled]):not([hidden])`;
focusableElements.push('[tabindex]:not([tabindex="-1"]):not([disabled])');
const TABBABLE_ELEMENT_SELECTOR = focusableElements.join(':not([hidden]):not([tabindex="-1"]),');
function getScopeRoot(scope) {
    return scope[0].parentElement;
}
function shouldContainFocus(scopeRef) {
    let scope = focusScopeTree.getTreeNode(activeScope);
    while(scope && scope.scopeRef !== scopeRef){
        if (scope.contain) {
            return false;
        }
        scope = scope.parent;
    }
    return true;
}
function useFocusContainment(scopeRef, contain) {
    const focusedNode = useRef();
    const raf = useRef(null);
    useLayoutEffect(()=>{
        const scope = scopeRef.current;
        if (!contain) {
            // if contain was changed, then we should cancel any ongoing waits to pull focus back into containment
            if (raf.current) {
                cancelAnimationFrame(raf.current);
                raf.current = null;
            }
            return;
        }
        // Handle the Tab key to contain focus within the scope
        const onKeyDown = (e)=>{
            if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey || scopeRef !== activeScope) {
                return;
            }
            const focusedElement = e.target.ownerDocument.activeElement;
            const scope = scopeRef.current;
            if (!isElementInScope(focusedElement, scope)) {
                return;
            }
            const walker = getFocusableTreeWalker(getScopeRoot(scope), {
                tabbable: true
            }, scope);
            walker.currentNode = focusedElement;
            let nextElement = e.shiftKey ? walker.previousNode() : walker.nextNode();
            if (!nextElement) {
                walker.currentNode = e.shiftKey ? scope[scope.length - 1].nextElementSibling : scope[0].previousElementSibling;
                nextElement = e.shiftKey ? walker.previousNode() : walker.nextNode();
            }
            e.preventDefault();
            if (nextElement) {
                focusElement(nextElement, true);
            }
        };
        const onFocus = (e)=>{
            // If focusing an element in a child scope of the currently active scope, the child becomes active.
            // Moving out of the active scope to an ancestor is not allowed.
            if (!activeScope || isAncestorScope(activeScope, scopeRef)) {
                activeScope = scopeRef;
                focusedNode.current = e.target;
            } else if (scopeRef === activeScope && !isElementInChildScope(e.target, scopeRef)) {
                // If a focus event occurs outside the active scope (e.g. user tabs from browser location bar),
                // restore focus to the previously focused node or the first tabbable element in the active scope.
                if (focusedNode.current) {
                    focusedNode.current.focus();
                } else if (activeScope) {
                    focusFirstInScope(activeScope.current);
                }
            } else if (scopeRef === activeScope) {
                focusedNode.current = e.target;
            }
        };
        const onBlur = (e)=>{
            // Firefox doesn't shift focus back to the Dialog properly without this
            if (raf.current) {
                cancelAnimationFrame(raf.current);
            }
            raf.current = requestAnimationFrame(()=>{
                const ownerDocument = getOwnerDocument(e.target);
                // Use ownerDocument.activeElement instead of e.relatedTarget so we can tell if user clicked into iframe
                if (scopeRef === activeScope && !isElementInChildScope(ownerDocument.activeElement, scopeRef)) {
                    focusedNode.current = e.target;
                    focusedNode.current.focus();
                }
            });
        };
        const ownerDocument = getOwnerDocument(scope[0]);
        ownerDocument.addEventListener('keydown', onKeyDown, false);
        ownerDocument.addEventListener('focusin', onFocus, false);
        scope.forEach((element)=>element.addEventListener('focusin', onFocus, false));
        scope.forEach((element)=>element.addEventListener('focusout', onBlur, false));
        return ()=>{
            ownerDocument.removeEventListener('keydown', onKeyDown, false);
            ownerDocument.removeEventListener('focusin', onFocus, false);
            scope.forEach((element)=>element.removeEventListener('focusin', onFocus, false));
            scope.forEach((element)=>element.removeEventListener('focusout', onBlur, false));
        };
    }, [
        scopeRef,
        contain
    ]);
    // This is a useLayoutEffect so it is guaranteed to run before our async synthetic blur
    useLayoutEffect(()=>{
        return ()=>{
            if (raf.current) {
                cancelAnimationFrame(raf.current);
            }
        };
    }, [
        raf
    ]);
}
function isElementInAnyScope(element) {
    return isElementInChildScope(element);
}
function isElementInScope(element, scope) {
    return scope.some((node)=>node.contains(element));
}
function isElementInChildScope(element, scope = null) {
    // If the element is within a top layer element (e.g. toasts), always allow moving focus there.
    if (element instanceof Element && element.closest('[data-react-aria-top-layer]')) {
        return true;
    }
    // node.contains in isElementInScope covers child scopes that are also DOM children,
    // but does not cover child scopes in portals.
    for (const { scopeRef: s } of focusScopeTree.traverse(focusScopeTree.getTreeNode(scope))){
        if (isElementInScope(element, s.current)) {
            return true;
        }
    }
    return false;
}
/** @private */ export function isElementInChildOfActiveScope(element) {
    return isElementInChildScope(element, activeScope);
}
function isAncestorScope(ancestor, scope) {
    var _focusScopeTree_getTreeNode;
    let parent = (_focusScopeTree_getTreeNode = focusScopeTree.getTreeNode(scope)) === null || _focusScopeTree_getTreeNode === void 0 ? void 0 : _focusScopeTree_getTreeNode.parent;
    while(parent){
        if (parent.scopeRef === ancestor) {
            return true;
        }
        parent = parent.parent;
    }
    return false;
}
function focusElement(element, scroll = false) {
    if (element != null && !scroll) {
        try {
            focusSafely(element);
        } catch (err) {
        // ignore
        }
    } else if (element != null) {
        try {
            element.focus();
        } catch (err) {
        // ignore
        }
    }
}
function focusFirstInScope(scope, tabbable = true) {
    const sentinel = scope[0].previousElementSibling;
    let walker = getFocusableTreeWalker(getScopeRoot(scope), {
        tabbable
    }, scope);
    walker.currentNode = sentinel;
    let nextNode = walker.nextNode();
    // If the scope does not contain a tabbable element, use the first focusable element.
    if (tabbable && !nextNode) {
        walker = getFocusableTreeWalker(getScopeRoot(scope), {
            tabbable: false
        }, scope);
        walker.currentNode = sentinel;
        nextNode = walker.nextNode();
    }
    focusElement(nextNode);
}
function useAutoFocus(scopeRef, autoFocus) {
    const autoFocusRef = React.useRef(autoFocus);
    useEffect(()=>{
        if (autoFocusRef.current) {
            activeScope = scopeRef;
            const ownerDocument = getOwnerDocument(scopeRef.current[0]);
            if (!isElementInScope(ownerDocument.activeElement, activeScope.current)) {
                focusFirstInScope(scopeRef.current);
            }
        }
        autoFocusRef.current = false;
    }, [
        scopeRef
    ]);
}
function useActiveScopeTracker(scopeRef, restore, contain) {
    // tracks the active scope, in case restore and contain are both false.
    // if either are true, this is tracked in useRestoreFocus or useFocusContainment.
    useLayoutEffect(()=>{
        if (restore || contain) {
            return;
        }
        const scope = scopeRef.current;
        const ownerDocument = getOwnerDocument(scope[0]);
        const onFocus = (e)=>{
            const target = e.target;
            if (isElementInScope(target, scopeRef.current)) {
                activeScope = scopeRef;
            } else if (!isElementInAnyScope(target)) {
                activeScope = null;
            }
        };
        ownerDocument.addEventListener('focusin', onFocus, false);
        scope.forEach((element)=>element.addEventListener('focusin', onFocus, false));
        return ()=>{
            ownerDocument.removeEventListener('focusin', onFocus, false);
            scope.forEach((element)=>element.removeEventListener('focusin', onFocus, false));
        };
    }, [
        scopeRef,
        restore,
        contain
    ]);
}
function shouldRestoreFocus(scopeRef) {
    let scope = focusScopeTree.getTreeNode(activeScope);
    while(scope && scope.scopeRef !== scopeRef){
        if (scope.nodeToRestore) {
            return false;
        }
        scope = scope.parent;
    }
    return (scope === null || scope === void 0 ? void 0 : scope.scopeRef) === scopeRef;
}
function useRestoreFocus(scopeRef, restoreFocus, contain) {
    // create a ref during render instead of useLayoutEffect so the active element is saved before a child with autoFocus=true mounts.
    const nodeToRestoreRef = useRef(typeof document !== 'undefined' ? getOwnerDocument(scopeRef.current[0]).activeElement : null);
    // restoring scopes should all track if they are active regardless of contain, but contain already tracks it plus logic to contain the focus
    // restoring-non-containing scopes should only care if they become active so they can perform the restore
    useLayoutEffect(()=>{
        const scope = scopeRef.current;
        const ownerDocument = getOwnerDocument(scope[0]);
        if (!restoreFocus || contain) {
            return;
        }
        const onFocus = ()=>{
            // If focusing an element in a child scope of the currently active scope, the child becomes active.
            // Moving out of the active scope to an ancestor is not allowed.
            if ((!activeScope || isAncestorScope(activeScope, scopeRef)) && isElementInScope(ownerDocument.activeElement, scopeRef.current)) {
                activeScope = scopeRef;
            }
        };
        ownerDocument.addEventListener('focusin', onFocus, false);
        scope.forEach((element)=>element.addEventListener('focusin', onFocus, false));
        return ()=>{
            ownerDocument.removeEventListener('focusin', onFocus, false);
            scope.forEach((element)=>element.removeEventListener('focusin', onFocus, false));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        scopeRef,
        contain
    ]);
    useLayoutEffect(()=>{
        const nodeToRestore = nodeToRestoreRef.current;
        const ownerDocument = getOwnerDocument(nodeToRestore);
        if (!restoreFocus) {
            return;
        }
        // Handle the Tab key so that tabbing out of the scope goes to the next element
        // after the node that had focus when the scope mounted. This is important when
        // using portals for overlays, so that focus goes to the expected element when
        // tabbing out of the overlay.
        const onKeyDown = (e)=>{
            const ownerDocument = getOwnerDocument(e.target);
            if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey || !shouldContainFocus(scopeRef)) {
                return;
            }
            const focusedElement = ownerDocument.activeElement;
            if (!isElementInScope(focusedElement, scopeRef.current)) {
                return;
            }
            let nodeToRestore = focusScopeTree.getTreeNode(scopeRef).nodeToRestore;
            // Create a DOM tree walker that matches all tabbable elements
            const walker = getFocusableTreeWalker(ownerDocument.body, {
                tabbable: true
            });
            // Find the next tabbable element after the currently focused element
            walker.currentNode = focusedElement;
            let nextElement = e.shiftKey ? walker.previousNode() : walker.nextNode();
            if (!ownerDocument.body.contains(nodeToRestore) || nodeToRestore === ownerDocument.body) {
                nodeToRestore = null;
                focusScopeTree.getTreeNode(scopeRef).nodeToRestore = null;
            }
            // If there is no next element, or it is outside the current scope, move focus to the
            // next element after the node to restore to instead.
            if ((!nextElement || !isElementInScope(nextElement, scopeRef.current)) && nodeToRestore) {
                walker.currentNode = nodeToRestore;
                // Skip over elements within the scope, in case the scope immediately follows the node to restore.
                do {
                    nextElement = e.shiftKey ? walker.previousNode() : walker.nextNode();
                }while (isElementInScope(nextElement, scopeRef.current))
                e.preventDefault();
                e.stopPropagation();
                if (nextElement) {
                    focusElement(nextElement, true);
                } else {
                    // If there is no next element and the nodeToRestore isn't within a FocusScope (i.e. we are leaving the top level focus scope)
                    // then move focus to the body.
                    // Otherwise restore focus to the nodeToRestore (e.g menu within a popover -> tabbing to close the menu should move focus to menu trigger)
                    if (!isElementInAnyScope(nodeToRestore)) {
                        focusedElement.blur();
                    } else {
                        focusElement(nodeToRestore, true);
                    }
                }
            }
        };
        if (!contain) {
            ownerDocument.addEventListener('keydown', onKeyDown, true);
        }
        return ()=>{
            if (!contain) {
                ownerDocument.removeEventListener('keydown', onKeyDown, true);
            }
        };
    }, [
        scopeRef,
        restoreFocus,
        contain
    ]);
    // useLayoutEffect instead of useEffect so the active element is saved synchronously instead of asynchronously.
    useLayoutEffect(()=>{
        if (!restoreFocus) {
            return;
        }
        const nodeToRestore = nodeToRestoreRef.current;
        focusScopeTree.getTreeNode(scopeRef).nodeToRestore = nodeToRestore;
        const ownerDocument = getOwnerDocument(nodeToRestore);
        return ()=>{
            const nodeToRestore = focusScopeTree.getTreeNode(scopeRef).nodeToRestore;
            // if we already lost focus to the body and this was the active scope, then we should attempt to restore
            if (restoreFocus && nodeToRestore && // eslint-disable-next-line react-hooks/exhaustive-deps
            (isElementInScope(ownerDocument.activeElement, scopeRef.current) || ownerDocument.activeElement === ownerDocument.body && shouldRestoreFocus(scopeRef))) {
                // freeze the focusScopeTree so it persists after the raf, otherwise during unmount nodes are removed from it
                const clonedTree = focusScopeTree.clone();
                requestAnimationFrame(()=>{
                    // Only restore focus if we've lost focus to the body, the alternative is that focus has been purposefully moved elsewhere
                    if (ownerDocument.activeElement === ownerDocument.body) {
                        // look up the tree starting with our scope to find a nodeToRestore still in the DOM
                        let treeNode = clonedTree.getTreeNode(scopeRef);
                        while(treeNode){
                            if (treeNode.nodeToRestore && ownerDocument.body.contains(treeNode.nodeToRestore)) {
                                focusElement(treeNode.nodeToRestore);
                                return;
                            }
                            treeNode = treeNode.parent;
                        }
                        // If no nodeToRestore was found, focus the first element in the nearest
                        // ancestor scope that is still in the tree.
                        treeNode = clonedTree.getTreeNode(scopeRef);
                        while(treeNode){
                            if (treeNode.scopeRef && focusScopeTree.getTreeNode(treeNode.scopeRef)) {
                                focusFirstInScope(treeNode.scopeRef.current, true);
                                return;
                            }
                            treeNode = treeNode.parent;
                        }
                    }
                });
            }
        };
    }, [
        scopeRef,
        restoreFocus
    ]);
}
/**
 * Create a [TreeWalker]{@link https://developer.mozilla.org/en-US/docs/Web/API/TreeWalker}
 * that matches all focusable/tabbable elements.
 */ export function getFocusableTreeWalker(root, opts, scope) {
    const selector = (opts === null || opts === void 0 ? void 0 : opts.tabbable) ? TABBABLE_ELEMENT_SELECTOR : FOCUSABLE_ELEMENT_SELECTOR;
    const walker = getOwnerDocument(root).createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
        acceptNode (node) {
            var _opts_from;
            // Skip nodes inside the starting node.
            if (opts === null || opts === void 0 ? void 0 : (_opts_from = opts.from) === null || _opts_from === void 0 ? void 0 : _opts_from.contains(node)) {
                return NodeFilter.FILTER_REJECT;
            }
            if (node.matches(selector) && isElementVisible(node) && (!scope || isElementInScope(node, scope)) && (!(opts === null || opts === void 0 ? void 0 : opts.accept) || opts.accept(node))) {
                return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_SKIP;
        }
    });
    if (opts === null || opts === void 0 ? void 0 : opts.from) {
        walker.currentNode = opts.from;
    }
    return walker;
}
/**
 * Creates a FocusManager object that can be used to move focus within an element.
 */ export function createFocusManager(ref, defaultOptions = {}) {
    return {
        focusNext (opts = {}) {
            const root = ref.current;
            if (!root) {
                return;
            }
            const { from, tabbable = defaultOptions.tabbable, wrap = defaultOptions.wrap, accept = defaultOptions.accept } = opts;
            const ownerDocument = getOwnerDocument(root);
            const node = from || ownerDocument.activeElement;
            const walker = getFocusableTreeWalker(root, {
                tabbable,
                accept
            });
            if (root.contains(node)) {
                walker.currentNode = node;
            }
            let nextNode = walker.nextNode();
            if (!nextNode && wrap) {
                walker.currentNode = root;
                nextNode = walker.nextNode();
            }
            if (nextNode) {
                focusElement(nextNode, true);
            }
            return nextNode;
        },
        focusPrevious (opts = defaultOptions) {
            const root = ref.current;
            if (!root) {
                return;
            }
            const { from, tabbable = defaultOptions.tabbable, wrap = defaultOptions.wrap, accept = defaultOptions.accept } = opts;
            const ownerDocument = getOwnerDocument(root);
            const node = from || ownerDocument.activeElement;
            const walker = getFocusableTreeWalker(root, {
                tabbable,
                accept
            });
            if (root.contains(node)) {
                walker.currentNode = node;
            } else {
                const next = last(walker);
                if (next) {
                    focusElement(next, true);
                }
                return next;
            }
            let previousNode = walker.previousNode();
            if (!previousNode && wrap) {
                walker.currentNode = root;
                previousNode = last(walker);
            }
            if (previousNode) {
                focusElement(previousNode, true);
            }
            return previousNode;
        },
        focusFirst (opts = defaultOptions) {
            const root = ref.current;
            if (!root) {
                return;
            }
            const { tabbable = defaultOptions.tabbable, accept = defaultOptions.accept } = opts;
            const walker = getFocusableTreeWalker(root, {
                tabbable,
                accept
            });
            const nextNode = walker.nextNode();
            if (nextNode) {
                focusElement(nextNode, true);
            }
            return nextNode;
        },
        focusLast (opts = defaultOptions) {
            const root = ref.current;
            if (!root) {
                return;
            }
            const { tabbable = defaultOptions.tabbable, accept = defaultOptions.accept } = opts;
            const walker = getFocusableTreeWalker(root, {
                tabbable,
                accept
            });
            const next = last(walker);
            if (next) {
                focusElement(next, true);
            }
            return next;
        }
    };
}
function last(walker) {
    let next;
    let last;
    do {
        last = walker.lastChild();
        if (last) {
            next = last;
        }
    }while (last)
    return next;
}
class Tree {
    get size() {
        return this.fastMap.size;
    }
    getTreeNode(data) {
        return this.fastMap.get(data);
    }
    addTreeNode(scopeRef, parent, nodeToRestore) {
        const parentNode = this.fastMap.get(parent !== null && parent !== void 0 ? parent : null);
        const node = new TreeNode({
            scopeRef
        });
        parentNode.addChild(node);
        node.parent = parentNode;
        this.fastMap.set(scopeRef, node);
        if (nodeToRestore) {
            node.nodeToRestore = nodeToRestore;
        }
    }
    addNode(node) {
        this.fastMap.set(node.scopeRef, node);
    }
    removeTreeNode(scopeRef) {
        // never remove the root
        if (scopeRef === null) {
            return;
        }
        const node = this.fastMap.get(scopeRef);
        const parentNode = node.parent;
        // when we remove a scope, check if any sibling scopes are trying to restore focus to something inside the scope we're removing
        // if we are, then replace the siblings restore with the restore from the scope we're removing
        for (const current of this.traverse()){
            if (current !== node && node.nodeToRestore && current.nodeToRestore && node.scopeRef.current && isElementInScope(current.nodeToRestore, node.scopeRef.current)) {
                current.nodeToRestore = node.nodeToRestore;
            }
        }
        const children = node.children;
        parentNode.removeChild(node);
        if (children.size > 0) {
            children.forEach((child)=>parentNode.addChild(child));
        }
        this.fastMap.delete(node.scopeRef);
    }
    // Pre Order Depth First
    *traverse(node = this.root) {
        if (node.scopeRef != null) {
            yield node;
        }
        if (node.children.size > 0) {
            for (const child of node.children){
                yield* this.traverse(child);
            }
        }
    }
    clone() {
        const newTree = new Tree();
        for (const node of this.traverse()){
            newTree.addTreeNode(node.scopeRef, node.parent.scopeRef, node.nodeToRestore);
        }
        return newTree;
    }
    constructor(){
        _define_property(this, "root", void 0);
        _define_property(this, "fastMap", new Map());
        this.root = new TreeNode({
            scopeRef: null
        });
        this.fastMap.set(null, this.root);
    }
}
class TreeNode {
    addChild(node) {
        this.children.add(node);
        node.parent = this;
    }
    removeChild(node) {
        this.children.delete(node);
        node.parent = undefined;
    }
    constructor(props){
        _define_property(this, "scopeRef", void 0);
        _define_property(this, "nodeToRestore", void 0);
        _define_property(this, "parent", void 0);
        _define_property(this, "children", new Set());
        _define_property(this, "contain", false);
        this.scopeRef = props.scopeRef;
    }
}
export let focusScopeTree = new Tree();

//# sourceMappingURL=FocusScope.js.map